import axios from 'axios';
import { User } from '@/types/appointment';
import type { ConsultationTestWaiver } from '@/types/consultationEligibility';
import { getAuthHeader } from '@/utils/auth';

// File has users and user program enrollments APIs

const apiUrl = 'users/users';
// const apiUrl = "users/users-simple";

interface UserProfile {
  user_profile_id: string;
  user?: string;
  user_id?: string;
  username?: string;
  uuid?: string;
  id?: string;
  client_id: string | null;
  date_joined?: string | null;
  email: string;
  first_name: string;
  last_name: string;
  title: string | null;
  gender: string;
  date_of_birth: string | null;
  phone_number: string;
  phone_country_code: string;
  alternate_phone: string | null;
  alternate_phone_country_code: string | null;
  alternate_email: string | null;
  preferred_contact_method: string;
  city: string;
  state: string | null;
  country: string | null;
  zipcode: string | null;
  address_line_1: string | null;
  address_line_2: string | null;
  house_no: string | null;
  street: string | null;
  landmark: string | null;
  profile_picture: string | null;
  role: string;
  occupation: string;
  relationship_status: string;
  relationship_status_custom: string | null;
  organization: string | null;
  clinic: string | null;
  emergency_contact_name: string | null;
  emergency_contact_relation: string | null;
  emergency_contact_number: string | null;
  emergency_contact_number_country_code: string | null;
  appointment_duration: string;
  hipaa_consent: boolean;
  hipaa_consent_date: string | null;
  hipaa_consent_version: string | null;
  health_goals: Record<string, any>;
  client_story: Record<string, any>;
  lifestyle_history: Record<string, any>;
  family_history: Record<string, any>;
  // Pod membership and the two columns the doctor-assignment matcher routes on.
  // Both are returned by GET /users/user-profiles/{id}; see src/types/user.ts.
  pods?: Array<{
    pod_id: string;
    name: string;
    type?: string;
    role?: string;
    assignment_type?: string;
    association_type: 'member' | 'client';
    is_active: boolean;
  }>;
  primary_language?: string | null;
  secondary_language?: string | null;
}

export async function getUser(userId: string, accessToken: string): Promise<any> {
  return axios.get(`${apiUrl}/${userId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getUserByUsername(username: string, accessToken: string): Promise<any> {
  return axios.get(`${apiUrl}/${username}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getUsers(accessToken?: string): Promise<any> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const response = await axios.get(apiUrl, { headers });

  // Handle paginated response structure { count: X, results: [...] }
  if (response.data && response.data.results) {
    return response.data.results;
  }

  return response.data;
}

export async function getDoctors(accessToken: string): Promise<any> {
  return axios.get(`${apiUrl}?role=DOCTOR`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function deleteUser(userName: string, accessToken?: string): Promise<any> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  const response = await axios.delete(`${apiUrl}/${userName}`, { headers });
  return response.data;
}

export async function createUser(userData: any, accessToken?: string): Promise<any> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  const { preferred_languages, ...rest } = userData;
  const payload: Record<string, any> =
    preferred_languages !== undefined ? { ...rest, preferences: preferred_languages } : rest;

  const hasFile = payload.signature instanceof File;

  if (hasFile) {
    const formData = new FormData();
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (value === null || value === undefined) return;
      if (value instanceof File || value instanceof Blob) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => formData.append(key, item));
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    // DO NOT set Content-Type header - axios will set it automatically with boundary
    const response = await axios.post(apiUrl, formData, { headers });
    return response.data;
  } else {
    const response = await axios.post(apiUrl, payload, { headers });
    return response.data;
  }
}

export async function createUserProfile(userData: any, accessToken?: string): Promise<any> {
  const headers: Record<string, string> = {};
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }

  // Check if userData contains file (signature)
  const hasFile = userData.signature instanceof File;

  if (hasFile) {
    const formData = new FormData();
    Object.keys(userData).forEach((key) => {
      const value = userData[key];
      if (value === null || value === undefined) return;
      if (value instanceof File || value instanceof Blob) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => formData.append(key, item));
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    // DO NOT set Content-Type header - axios will set it automatically with boundary
    const response = await axios.post('users/user-profiles', formData, { headers });
    return response.data;
  } else {
    const response = await axios.post('users/user-profiles', userData, { headers });
    return response.data;
  }
}

const API_TIMEOUT_MS = 30000;

const sanitizeErrorForLogging = (error: any): string => {
  if (!error) return 'Unknown error';
  if (typeof error === 'string') return error;
  if (error.message) return error.message;
  if (error.response?.status) {
    return `HTTP ${error.response.status}: ${error.response.statusText || 'Request failed'}`;
  }
  return 'Request failed';
};

const findUserByProfile = async (
  profileId: string,
  token: string,
  userData: any,
  profileResponse?: any
): Promise<string | null> => {
  const userIdentifier = userData.user || userData.username || userData.uuid;

  if (userIdentifier) {
    return userIdentifier;
  }

  if (profileResponse?.data?.user) {
    return profileResponse.data.user;
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  try {
    const updatedProfileResponse = await getUserProfile(profileId, token, {
      signal: controller.signal,
    });
    const profileData = updatedProfileResponse.data;
    if (profileData?.user) {
      return profileData.user;
    }
  } catch (fetchError: any) {
    if (fetchError.name !== 'AbortError') {
      console.debug('Could not fetch updated profile to get user identifier');
    }
  } finally {
    clearTimeout(timeoutId);
  }

  return null;
};

const buildSyncPayload = (updatePayload: any): Record<string, any> => {
  const syncPayload: Record<string, any> = {};

  const fieldsToSync = [
    'first_name',
    'last_name',
    'email',
    'phone_number',
    'phone_country_code',
    'alternate_phone',
    'alternate_phone_country_code',
    'alternate_email',
    'title',
    'gender',
    'date_of_birth',
    'occupation',
    'relationship_status',
    'relationship_status_custom',
    'address_line_1',
    'address_line_2',
    'house_no',
    'street',
    'landmark',
    'city',
    'state',
    'country',
    'zipcode',
    'role',
    'organization',
    'clinic',
    'emergency_contact_name',
    'emergency_contact_relation',
    'emergency_contact_number',
    'emergency_contact_number_country_code',
    'preferred_contact_method',
    'appointment_duration',
    'specialization',
    'registration_number',
    'registration_state',
  ];

  fieldsToSync.forEach((field) => {
    if (updatePayload[field] !== undefined) {
      syncPayload[field] = updatePayload[field];
    }
  });

  Object.keys(updatePayload).forEach((field) => {
    if (
      !fieldsToSync.includes(field) &&
      field !== 'signature' &&
      field !== 'signature_file_name' &&
      updatePayload[field] !== undefined
    ) {
      syncPayload[field] = updatePayload[field];
    }
  });

  return syncPayload;
};

const syncUserEndpoints = async (
  userId: string,
  syncPayload: Record<string, any>,
  token: string
): Promise<void> => {
  if (Object.keys(syncPayload).length === 0) {
    return;
  }

  const headers = { Authorization: `Bearer ${token}` };
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), API_TIMEOUT_MS);

  try {
    try {
      await axios.patch(
        `${apiUrl}/${userId}`,
        { Profile: syncPayload },
        { headers, signal: controller.signal }
      );
    } catch (syncError: any) {
      if (syncError.response?.status === 405) {
        console.debug('Users endpoint is read-only, backend should sync automatically');
        return;
      }

      if (syncError.name === 'AbortError') {
        console.debug('Sync request timed out');
        return;
      }

      try {
        await axios.patch(`${apiUrl}/${userId}`, syncPayload, {
          headers,
          signal: controller.signal,
        });
      } catch (retryError: any) {
        if (retryError.response?.status === 405) {
          console.debug('Users endpoint is read-only, backend should sync automatically');
          return;
        }
        if (retryError.name !== 'AbortError') {
          const sanitizedError = sanitizeErrorForLogging(retryError);
          console.debug(`Failed to sync users endpoint: ${sanitizedError}`);
        }
      }
    }
  } finally {
    clearTimeout(timeoutId);
  }
};

const updateUserProfile = async (
  profileId: string,
  updatePayload: any,
  token?: string
): Promise<any> => {
  const headers: Record<string, string> = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const { preferred_languages, ...rest } = updatePayload;
  const payload: Record<string, any> =
    preferred_languages !== undefined ? { ...rest, preferences: preferred_languages } : rest;

  const hasFile = payload.signature instanceof File;

  if (hasFile) {
    const formData = new FormData();
    Object.keys(payload).forEach((key) => {
      const value = payload[key];
      if (value === null || value === undefined) return;
      if (value instanceof File || value instanceof Blob) {
        formData.append(key, value);
      } else if (Array.isArray(value)) {
        value.forEach((item) => formData.append(key, item));
      } else if (typeof value === 'object') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    const response = await axios.patch(`users/user-profiles/${profileId}`, formData, { headers });
    return response;
  } else {
    const response = await axios.patch(`users/user-profiles/${profileId}`, payload, {
      headers,
    });
    return response;
  }
};

export async function updateUser(userData: any, accessToken?: string): Promise<any> {
  const userProfileId = userData.user_profile_id || userData.user;

  if (!userProfileId) {
    throw new Error('User profile identifier is required for profile update');
  }

  const {
    user,
    user_profile_id,
    client_id,
    hipaa_consent_date,
    created_at,
    updated_at,
    username,
    uuid,
    ...rest
  } = userData;

  const addressFields = ['address_line_1', 'address_line_2', 'city', 'state', 'country', 'zipcode'];
  const updatePayload = Object.fromEntries(
    Object.entries(rest).filter(([key]) => !addressFields.includes(key))
  );

  const profileResponse = await updateUserProfile(userProfileId, updatePayload, accessToken);

  if (accessToken) {
    try {
      const userIdentifier = await findUserByProfile(
        userProfileId,
        accessToken,
        userData,
        profileResponse
      );

      if (userIdentifier) {
        const syncPayload = buildSyncPayload(updatePayload);
        await syncUserEndpoints(userIdentifier, syncPayload, accessToken);
      } else {
        console.debug('Could not determine user identifier to sync users endpoint');
      }
    } catch (error: any) {
      const sanitizedError = sanitizeErrorForLogging(error);
      console.debug(`Could not sync users endpoint: ${sanitizedError}`);
    }
  }

  return profileResponse.data;
}

export async function getUsersByClient(clientId: string, accessToken: string): Promise<any> {
  return axios.get<User>(`${apiUrl}/${clientId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

interface ProgramData {
  program_steps: {
    profile: 'true' | 'false';
    upload_reports: 'true' | 'false';
    in_center: 'true' | 'false'; // New field
    thyrocare: 'true' | 'false';
    neurowyzer: 'true' | 'false';
    intake_call: 'true' | 'false';
    thrive_check: 'true' | 'false'; // New field
    concierge_call: 'true' | 'false'; // New field
  };
  checklist_start_date: string | null; // ISO datetime
  intake_call_id: string[];
  intake_form_id: string[]; // New field
  thrive_check_id: string[]; // New field
  concierge_call_id: string[]; // New field
  thyrocare_order_id: string[];
  neurowyzer_order_id: string[];
  in_clinic_appointment_id: string[]; // New field
  integration_reference_id: string; // Auto-generated 8-char ID
  is_thyrocare_rescheduled: 'true' | 'false';
}

interface WorkflowTask {
  task_id: string;
  title: string;
  label: string;
  who: string;
  category: string;
  description: string;
  order: number;
  is_required: boolean;
  completed: boolean;
  completed_at: string | null; // ISO datetime
  completed_by: string | null; // UserProfile ID
}

interface WorkflowChecklist {
  checklist_id: string;
  title: string;
  status: 'DO' | 'DOING' | 'DONE';
  template_name: string;
  created_at: string; // ISO datetime
  tasks: WorkflowTask[];
}

interface ProgramEnrollment {
  user_program_enrollment_id?: string;
  user: string;
  program: string;
  program_name?: string; // READ-ONLY
  user_first_name?: string; // READ-ONLY
  user_last_name?: string; // READ-ONLY
  start_date: string;
  end_date: string | null;
  is_active: boolean;
  delivery_mode: 'online' | 'offline' | 'hybrid';
  notes?: string;

  // JSON Data fields
  extra_data?: Record<string, any>;
  program_data?: ProgramData | Record<string, unknown>;
  add_on_tests?: Record<string, any>;

  // Workflow integration
  checklist?: WorkflowChecklist | null; // READ-ONLY embedded checklist data
  checklist_template_name?: string; // WRITE-ONLY for creation

  // Progress tracking
  program_progress?: number; // READ-ONLY percentage (0-100)
  workflow_checklist_progress?: number; // READ-ONLY percentage (0-100)

  // Financial tracking
  total_amount?: number | null;
  amount_paid?: number | null;
  enrolled_by_name?: string | null;

  // Billing information
  billing_status?: 'NOT_BILLED' | 'BILLED' | 'PAID' | 'WAIVED'; // READ-ONLY
  bill_id?: string; // READ-ONLY
  bill_number?: string; // READ-ONLY
  bill_amount?: number; // READ-ONLY
  bill_status?: string; // READ-ONLY
  waiver_reason?: string; // READ-ONLY
  waived_by?: string; // READ-ONLY
  waived_at?: string; // READ-ONLY

  show_intake_call?: 'true' | 'false' | string;
}

export async function getAllUserProgramEnrollments(accessToken: string): Promise<any> {
  return axios.get(`users/user-program-enrollments`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getUserProgramEnrollments(
  userId: string,
  accessToken: string,
  config?: { signal?: AbortSignal }
): Promise<any> {
  return axios.get(`users/user-program-enrollments?user=${userId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    ...config,
  });
}

/** Enrollments for a page of users — one request instead of N per-user calls. */
const ENROLLMENTS_PAGE_SIZE_FLOOR = 50;
const ENROLLMENTS_PAGE_SIZE_CAP = 500;

export async function getUserProgramEnrollmentsForUsers(
  userIds: string[],
  accessToken: string,
  config?: { signal?: AbortSignal }
): Promise<any> {
  const uniqueIds = [...new Set(userIds)].filter(
    (id): id is string => typeof id === 'string' && id.length > 0
  );
  if (uniqueIds.length === 0) {
    return { data: [] };
  }
  const params = new URLSearchParams();
  params.append('users', uniqueIds.join(','));
  // 4x: a client can have several historic enrollments. Floor 50 covers a
  // small page; cap 500 stays under typical API page-size limits.
  const pageSize = Math.min(
    Math.max(uniqueIds.length * 4, ENROLLMENTS_PAGE_SIZE_FLOOR),
    ENROLLMENTS_PAGE_SIZE_CAP
  );
  params.append('page_size', String(pageSize));
  return axios.get(`users/user-program-enrollments?${params.toString()}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    ...config,
  });
}

export async function enrollUserInProgram(
  enrollmentData: ProgramEnrollment,
  accessToken: string
): Promise<any> {
  return axios.post(`users/user-program-enrollments`, enrollmentData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getProgramEnrollment(
  enrollmentId: string,
  accessToken: string
): Promise<any> {
  return axios.get(`users/user-program-enrollments/${enrollmentId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function updateProgramEnrollment(
  enrollmentId: string,
  enrollmentData: Partial<ProgramEnrollment>,
  accessToken: string
): Promise<any> {
  return axios.patch(`users/user-program-enrollments/${enrollmentId}`, enrollmentData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function partialUpdateProgramEnrollment(
  enrollmentId: string,
  enrollmentData: Partial<ProgramEnrollment>,
  accessToken: string,
  config?: { signal?: AbortSignal }
): Promise<any> {
  return axios.patch(`users/user-program-enrollments/${enrollmentId}`, enrollmentData, {
    headers: { Authorization: `Bearer ${accessToken}` },
    ...config,
  });
}

export async function deleteProgramEnrollment(
  enrollmentId: string,
  accessToken: string
): Promise<any> {
  return axios.delete(`users/user-program-enrollments/${enrollmentId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getUserswithProfile(accessToken: string): Promise<any> {
  return axios.get(`users/user-profiles`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

/**
 * Search users by name or email
 */
export async function searchUsers(
  query: string,
  accessToken: string,
  role?: string
): Promise<any[]> {
  const params = new URLSearchParams({ search: query });
  if (role) {
    params.append('role', role);
  }
  params.append('page_size', '500');
  const headers = { Authorization: `Bearer ${accessToken}` };
  const response = await axios.get(`users/user-profiles?${params.toString()}`, { headers });

  if (response.data && response.data.results) {
    let allResults = [...response.data.results];
    let nextUrl = response.data.next;
    while (nextUrl) {
      const nextResponse = await axios.get(nextUrl, { headers });
      allResults = [...allResults, ...(nextResponse.data.results || [])];
      nextUrl = nextResponse.data.next;
    }
    return allResults;
  }
  return response.data || [];
}

export async function getUserProfile(
  userId: string,
  accessToken: string,
  config?: { signal?: AbortSignal }
): Promise<{ data: UserProfile }> {
  return axios.get(`users/user-profiles/${userId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
    ...config,
  });
}

export async function updateHipaaConsent(
  userId: string,
  consentData: {
    hipaa_consent: boolean;
    hipaa_consent_date: string;
    hipaa_consent_version: string;
  },
  accessToken: string
): Promise<any> {
  return axios.patch(`users/user-profiles/${userId}`, consentData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function patchUserProfile(
  userProfileId: string,
  payload: Record<string, any>,
  accessToken: string
): Promise<any> {
  return axios.patch(`users/user-profiles/${userProfileId}`, payload, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function patchUserAccount(
  userId: string,
  payload: Record<string, any>,
  accessToken: string
): Promise<any> {
  const headers = { Authorization: `Bearer ${accessToken}` };
  try {
    return await axios.patch(`${apiUrl}/${userId}`, { Profile: payload }, { headers });
  } catch (error: any) {
    if (error?.response?.status === 405) {
      throw error;
    }
    return axios.patch(`${apiUrl}/${userId}`, payload, { headers });
  }
}

export async function updateUserEmailViaAuthManagement(
  userProfileId: string,
  newEmail: string,
  accessToken: string,
  sendInvitation = false
): Promise<any> {
  const endpoint = `users/auth-management/${userProfileId}/update-email`;
  const headers = { Authorization: `Bearer ${accessToken}` };
  const normalizedEmail = (newEmail || '').trim().toLowerCase();
  const extractErrorMessage = (error: any): string => {
    const responseData = error?.response?.data;
    if (typeof responseData === 'string') return responseData;
    if (Array.isArray(responseData)) return responseData.join(', ');
    if (typeof responseData?.detail === 'string') return responseData.detail;
    if (Array.isArray(responseData?.detail)) return responseData.detail.join(', ');
    if (typeof responseData?.message === 'string') return responseData.message;
    if (typeof error?.message === 'string') return error.message;
    try {
      return JSON.stringify(responseData || '');
    } catch {
      return '';
    }
  };
  const hasClientIdValidationIssue = (error: any): boolean =>
    extractErrorMessage(error).includes('Client ID must be in format');
  const clientIdCurrentFormat = /^[A-Z]{3}\d{2}\d{6}$/;
  const clientIdLegacyFormat = /^[A-Z]{5}\d{4}$/;
  const generateCompliantClientId = (
    currentClientId?: string | null,
    profileId?: string
  ): string => {
    const normalizedCurrent = (currentClientId || '').trim().toUpperCase();
    if (
      normalizedCurrent &&
      (clientIdCurrentFormat.test(normalizedCurrent) ||
        clientIdLegacyFormat.test(normalizedCurrent))
    ) {
      return normalizedCurrent;
    }

    const year = new Date().getFullYear().toString().slice(-2);
    const seed = `${normalizedCurrent}${profileId || ''}`;
    let hash = 0;
    for (let i = 0; i < seed.length; i += 1) {
      hash = (hash * 31 + seed.charCodeAt(i)) % 1_000_000;
    }
    const suffix = String(Math.abs(hash)).padStart(6, '0').slice(-6);
    return `TMP${year}${suffix}`;
  };
  const resolveUserIdentifierFromProfile = async (): Promise<string | null> => {
    try {
      const profileResponse = await getUserProfile(userProfileId, accessToken);
      const profileData = profileResponse?.data;
      const resolvedUserId =
        profileData?.user ||
        profileData?.user_id ||
        profileData?.username ||
        profileData?.uuid ||
        profileData?.id ||
        null;
      return resolvedUserId ? String(resolvedUserId) : null;
    } catch (error) {
      console.debug('Could not resolve user identifier from user profile', error);
      return null;
    }
  };
  const fallbackToAccountEmailUpdate = async () => {
    const userIdentifier = await resolveUserIdentifierFromProfile();
    if (!userIdentifier) {
      throw new Error('Unable to resolve account user for email update fallback');
    }
    return patchUserAccount(userIdentifier, { email: normalizedEmail }, accessToken);
  };
  const persistEmailOnProfile = async () => {
    try {
      await patchUserProfile(
        userProfileId,
        {
          email: normalizedEmail,
        },
        accessToken
      );
    } catch (profileUpdateError: any) {
      if (!hasClientIdValidationIssue(profileUpdateError)) {
        throw profileUpdateError;
      }

      let existingClientId: string | null = null;
      try {
        const profileResponse = await getUserProfile(userProfileId, accessToken);
        const profileData = profileResponse?.data;
        existingClientId = profileData?.client_id || null;
      } catch (profileFetchError) {
        console.debug('Could not fetch profile for legacy client_id fallback', profileFetchError);
      }

      const fallbackClientId = generateCompliantClientId(existingClientId, userProfileId);
      await patchUserProfile(
        userProfileId,
        {
          email: normalizedEmail,
          client_id: fallbackClientId,
        },
        accessToken
      );
    }

    // Verify persistence so UI doesn't report success on stale backend state.
    const refreshedProfileResponse = await getUserProfile(userProfileId, accessToken);
    const refreshedProfileData = refreshedProfileResponse?.data;
    const persistedEmail = (refreshedProfileData?.email || '').toString().toLowerCase();
    if (persistedEmail && persistedEmail !== normalizedEmail) {
      throw new Error('Email update did not persist on profile');
    }
  };
  const shouldFallbackFromAuthManagement = (status?: number) =>
    status === 401 || status === 403 || status === 404 || status === 405;
  const shouldRetryWithLegacyPayload = (status?: number) => status === 400 || status === 422;

  // Step 1 (optional): auth-management endpoint for invitation-driven flows.
  if (sendInvitation) {
    try {
      await axios.post(
        endpoint,
        {
          new_email: normalizedEmail,
          send_invitation: sendInvitation,
        },
        { headers }
      );
    } catch (authError: any) {
      const authStatus = authError?.response?.status;
      if (shouldRetryWithLegacyPayload(authStatus)) {
        try {
          await axios.post(
            endpoint,
            {
              email: normalizedEmail,
              send_invitation: sendInvitation,
            },
            { headers }
          );
        } catch (legacyPayloadError: any) {
          const legacyStatus = legacyPayloadError?.response?.status;
          if (!shouldFallbackFromAuthManagement(legacyStatus)) {
            throw legacyPayloadError;
          }
        }
      } else if (!shouldFallbackFromAuthManagement(authStatus)) {
        throw authError;
      }
    }
  }

  // Step 2: always persist on user profile for data consistency.
  await persistEmailOnProfile();

  // Step 3: best-effort sync users/users endpoint; don't fail successful profile updates.
  try {
    await fallbackToAccountEmailUpdate();
  } catch (accountSyncError) {
    console.debug('Best-effort account email sync failed after profile update', accountSyncError);
  }

  return { success: true, email: normalizedEmail };
}

export interface UpdatePasswordRequest {
  old_password: string;
  new_password: string;
}

export interface UpdatePasswordResponse {
  old_password_updated_at: string | null;
  new_password_updated_at: string | null;
}

export async function updatePasswordViaAuthManagement(
  payload: UpdatePasswordRequest,
  accessToken: string
): Promise<{ data: UpdatePasswordResponse }> {
  return axios.post(`users/auth-management/update-password`, payload, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function updateProgramEnrollmentTasks(
  enrollmentId: string,
  taskUpdates: Array<{
    task_id: string;
    completed: boolean;
    notes?: string;
  }>,
  accessToken: string
): Promise<any> {
  return axios.patch(
    `users/user-program-enrollments/${enrollmentId}`,
    {
      task_updates: taskUpdates,
    },
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );
}

// Add-On Enrollment Interfaces and APIs

interface UserAddOnEnrollment {
  user_addon_enrollment_id?: string;
  user: string; // UserProfile ID
  addon_id: string; // Add-on identifier
  addon_name: string; // Human-readable add-on name
  start_date?: string; // ISO date, defaults to today
  end_date?: string | null; // ISO date, null for indefinite
  is_active?: boolean; // Default: true
  notes?: string; // Additional notes
  addon_data?: Record<string, any>; // Add-on specific data

  // READ-ONLY fields
  user_first_name?: string; // Populated from user profile
  user_last_name?: string; // Populated from user profile

  // Financial tracking
  total_amount?: number | null;
  amount_paid?: number | null;
  enrolled_by_name?: string | null;

  // Billing information (READ-ONLY)
  billing_status?: 'NOT_BILLED' | 'BILLED' | 'PAID' | 'WAIVED';
  bill_id?: string;
  bill_number?: string;
  bill_amount?: number;
  bill_status?: string;
  waiver_reason?: string;
  waived_by?: string;
  waived_at?: string; // ISO datetime
}

interface BillingStatusResponse {
  billing_status: string;
  bill_id?: string;
  bill_number?: string;
  bill_amount?: string;
  bill_status?: string;
  waiver_reason?: string | null;
  waived_by?: string | null;
  waived_at?: string | null;
}

interface WaiveBillingRequest {
  reason: string;
}

export async function getAllUserAddOnEnrollments(accessToken: string): Promise<any> {
  return axios.get(`users/user-addon-enrollments`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getUserAddOnEnrollments(userId: string, accessToken: string): Promise<any> {
  return axios.get(`users/user-addon-enrollments?user=${userId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getActiveUserAddOnEnrollments(
  userId: string,
  accessToken: string
): Promise<any> {
  return axios.get(`users/user-addon-enrollments/active?user_id=${userId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function createUserAddOnEnrollment(
  enrollmentData: UserAddOnEnrollment,
  accessToken: string
): Promise<any> {
  return axios.post(`users/user-addon-enrollments`, enrollmentData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getUserAddOnEnrollment(
  enrollmentId: string,
  accessToken: string
): Promise<any> {
  return axios.get(`users/user-addon-enrollments/${enrollmentId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function updateUserAddOnEnrollment(
  enrollmentId: string,
  enrollmentData: Partial<UserAddOnEnrollment>,
  accessToken: string
): Promise<any> {
  return axios.put(`users/user-addon-enrollments/${enrollmentId}`, enrollmentData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function partialUpdateUserAddOnEnrollment(
  enrollmentId: string,
  enrollmentData: Partial<UserAddOnEnrollment>,
  accessToken: string
): Promise<any> {
  return axios.patch(`users/user-addon-enrollments/${enrollmentId}`, enrollmentData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function deleteUserAddOnEnrollment(
  enrollmentId: string,
  accessToken: string
): Promise<any> {
  return axios.delete(`users/user-addon-enrollments/${enrollmentId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getUserAddOnEnrollmentBillingStatus(
  enrollmentId: string,
  accessToken: string
): Promise<{ data: BillingStatusResponse }> {
  return axios.get(`users/user-addon-enrollments/${enrollmentId}/billing-status`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function waiveUserAddOnEnrollmentBilling(
  enrollmentId: string,
  waiveData: WaiveBillingRequest,
  accessToken: string
): Promise<any> {
  return axios.post(`users/user-addon-enrollments/${enrollmentId}/waive-billing`, waiveData, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function waiveConsultationTest(
  body: { enrollment: string; requirement: string; reason?: string },
  accessToken: string
): Promise<{ data: ConsultationTestWaiver }> {
  return axios.post('users/consultation-test-waivers', body, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function unwaiveConsultationTest(waiverId: string, accessToken: string): Promise<any> {
  return axios.delete(`users/consultation-test-waivers/${waiverId}`, {
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

export async function getConsultationTestWaivers(
  enrollmentId: string,
  accessToken: string
): Promise<{ data: ConsultationTestWaiver[] }> {
  return axios.get('users/consultation-test-waivers', {
    params: { enrollment: enrollmentId },
    headers: { Authorization: `Bearer ${accessToken}` },
  });
}

/**
 * Export users to Excel in the same format as the bulk upload template
 */
export async function exportUsers(accessToken: string, roleFilter?: string): Promise<Blob> {
  const params: Record<string, string> = {};
  if (roleFilter) {
    params.role = roleFilter;
  }

  const response = await axios.get('users/user-profiles/export-users', {
    headers: { Authorization: `Bearer ${accessToken}` },
    params,
    responseType: 'blob', // Important for file download
  });

  return response.data;
}

/**
 * Resend invitation email to a user
 */
export interface ResendInvitationResponse {
  status: string;
  message: string;
  user_id: string;
  email_sent?: boolean;
}

export async function resendUserInvitation(
  username: string,
  accessToken: string
): Promise<ResendInvitationResponse> {
  const response = await axios.post(
    `${apiUrl}/${username}/reinvite`,
    {},
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    }
  );

  return response.data;
}
